// Admin → 통합/API — 외부 서비스, API 키, 웹훅, SSO, 연동 설정.
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { api } from "../../../shared/api/client";
import { I } from "../../../shared/Icon";
import { toast } from "../../../shared/stores/toasts";

// ── Types ─────────────────────────────────────────────────────────────────────

type IntegConfig = {
  smtp: { host: string; port: number; user: string; password: string; from_addr: string; tls: boolean };
  etax: { base_url: string; api_key: string; business_no: string };
  openbanking: { base_url: string; client_id: string; client_secret: string };
};

// ── Static data ───────────────────────────────────────────────────────────────

const SERVICES = [
  { id: "google", name: "Google Workspace",  desc: "메일, 캘린더, 드라이브 동기화", c: "#4285F4", connected: true  },
  { id: "ms",     name: "Microsoft 365",     desc: "메일, 캘린더, 팀즈",           c: "#0078D4", connected: false },
  { id: "slack",  name: "Slack",             desc: "알림 채널 통합",               c: "#4A154B", connected: true  },
  { id: "github", name: "GitHub Enterprise", desc: "PR/이슈 알림 + 결재 연동",     c: "#24292E", connected: true  },
  { id: "jira",   name: "Jira",              desc: "이슈 동기화",                  c: "#0052CC", connected: false },
  { id: "zoom",   name: "Zoom",              desc: "회의실 예약 연동",              c: "#2D8CFF", connected: true  },
  { id: "salesf", name: "Salesforce",        desc: "CRM 거래처 동기화",             c: "#00A1E0", connected: false },
  { id: "stripe", name: "Stripe",            desc: "결제 / 청구",                  c: "#635BFF", connected: true  },
];

const API_KEYS = [
  { id: "k1", name: "BI Dashboard 연동",    key: "tmpl_live_kdjf...8qz3", scope: "read",          created: "2026.04.02", lastUsed: "2시간 전", active: true  },
  { id: "k2", name: "Mobile Push 서비스",   key: "tmpl_live_pUsh...2k9x", scope: "notifications", created: "2026.03.18", lastUsed: "방금",     active: true  },
  { id: "k3", name: "ABC Corp 데이터 연동", key: "tmpl_live_aBc...9p2m",  scope: "read,export",   created: "2026.01.05", lastUsed: "어제",     active: true  },
  { id: "k4", name: "임시 — 마이그레이션", key: "tmpl_live_mig...x47k",  scope: "read,write",    created: "2026.05.15", lastUsed: "5/19",     active: false },
];

const WEBHOOKS = [
  { url: "https://hooks.slack.com/services/T..../...",  events: ["결재 승인", "결재 반려"], lastFired: "10분 전",  status: "정상" },
  { url: "https://api.abc.example.com/timple/notify",  events: ["거래처 변경"],            lastFired: "어제",     status: "정상" },
  { url: "https://ops.timple.kr/oncall",               events: ["시스템 알림"],            lastFired: "3시간 전", status: "정상" },
];

// ── Sub-tab toggle ─────────────────────────────────────────────────────────────

function TabBar({ tabs, active, onChange }: { tabs: { id: string; l: string }[]; active: string; onChange: (id: string) => void }) {
  return (
    <div style={{ display: "flex", gap: 4, background: "var(--panel)", borderRadius: 8, padding: 3, alignSelf: "flex-start", flexWrap: "wrap" }}>
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding: "6px 14px", fontSize: 12.5, fontWeight: 600,
            background: active === t.id ? "var(--surface)" : "transparent",
            color: active === t.id ? "var(--text)" : "var(--text-3)",
            border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
            boxShadow: active === t.id ? "var(--shadow-sm)" : "none",
          }}
        >
          {t.l}
        </button>
      ))}
    </div>
  );
}

// ── Shared form row ────────────────────────────────────────────────────────────

function FR({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "12px 18px", borderTop: "1px solid var(--border)", gap: 12 }}>
      <span style={{ width: 160, fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>{children}</div>
    </div>
  );
}

function TInput({ value, onChange, placeholder, type = "text", mono = false }: {
  value: string; onChange: (v: string) => void; placeholder?: string; type?: string; mono?: boolean;
}) {
  return (
    <input
      value={value}
      type={type}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        height: 34, padding: "0 10px", border: "1px solid var(--border-strong)",
        borderRadius: 6, background: "var(--surface)", color: "var(--text)",
        fontFamily: mono ? "var(--font-mono, monospace)" : "inherit", fontSize: 13, outline: "none", flex: 1,
      }}
    />
  );
}

// ── Services sub-tab ──────────────────────────────────────────────────────────

function ServicesTab() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
      {SERVICES.map((s) => (
        <div
          key={s.id}
          className="t-card"
          style={{ padding: 16, flexDirection: "row", alignItems: "center", gap: 12 }}
        >
          <div style={{
            width: 40, height: 40, borderRadius: 8, background: s.c, color: "white",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: 14, flexShrink: 0,
          }}>
            {s.name.slice(0, 1)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{s.name}</div>
            <div style={{ fontSize: 11.5, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.desc}</div>
          </div>
          {s.connected
            ? <button className="t-btn t-btn-outline" style={{ height: 30 }} onClick={() => toast.info("준비 중", `${s.name} 관리 화면은 준비 중입니다`)}>관리</button>
            : <button className="t-btn t-btn-primary" style={{ height: 30 }} onClick={() => toast.info("준비 중", `${s.name} 연동은 준비 중입니다`)}>연결</button>}
        </div>
      ))}
    </div>
  );
}

// ── API Keys sub-tab ──────────────────────────────────────────────────────────

function KeysTab() {
  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <span style={{ fontSize: 12.5, color: "var(--text-3)" }}>외부 시스템과 통신하기 위한 API 키. 비공개로 관리하세요.</span>
        <button className="t-btn t-btn-primary" style={{ marginLeft: "auto" }} onClick={() => toast.info("준비 중", "API 키 발급 기능은 준비 중입니다")}>
          <I.Plus size={13} /> 새 키 발급
        </button>
      </div>
      <div className="t-card" style={{ padding: 0, overflow: "auto" }}>
        <div style={{
          display: "flex", padding: "10px 18px",
          fontSize: 11, fontWeight: 600, color: "var(--text-3)",
          textTransform: "uppercase", letterSpacing: "0.06em",
          background: "var(--panel)", minWidth: 720,
        }}>
          <span style={{ flex: 1 }}>이름 / 키</span>
          <span style={{ width: 160 }}>범위</span>
          <span style={{ width: 110 }}>생성일</span>
          <span style={{ width: 100 }}>최근 사용</span>
          <span style={{ width: 70 }}>상태</span>
          <span style={{ width: 50 }} />
        </div>
        {API_KEYS.map((k) => (
          <div
            key={k.id}
            style={{
              display: "flex", padding: "12px 18px",
              borderTop: "1px solid var(--border)", fontSize: 13,
              alignItems: "center", minWidth: 720,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{k.name}</div>
              <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>{k.key}</div>
            </div>
            <span style={{ width: 160 }}>
              {k.scope.split(",").map((s) => (
                <span key={s} className="t-pill muted mono" style={{ marginRight: 3, fontSize: 9.5 }}>{s}</span>
              ))}
            </span>
            <span style={{ width: 110, color: "var(--text-3)" }} className="mono">{k.created}</span>
            <span style={{ width: 100, color: "var(--text-3)" }}>{k.lastUsed}</span>
            <span style={{ width: 70 }}>
              <span className={`t-pill ${k.active ? "ok" : "muted"}`}>{k.active ? "활성" : "만료"}</span>
            </span>
            <span style={{ width: 50, textAlign: "right" }}>
              <button className="t-iconbtn" style={{ width: 26, height: 26 }} onClick={() => toast.info("준비 중", "키 관리 기능은 준비 중입니다")}><I.More size={14} /></button>
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Webhooks sub-tab ──────────────────────────────────────────────────────────

function HooksTab() {
  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <span style={{ fontSize: 12.5, color: "var(--text-3)" }}>지정한 이벤트 발생 시 외부 URL로 POST 요청을 보냅니다.</span>
        <button className="t-btn t-btn-primary" style={{ marginLeft: "auto" }} onClick={() => toast.info("준비 중", "웹훅 등록 기능은 준비 중입니다")}>
          <I.Plus size={13} /> 새 웹훅
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {WEBHOOKS.map((h, i) => (
          <div key={i} className="t-card" style={{ padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span className="t-pill ok">{h.status}</span>
              <span className="mono" style={{ fontSize: 12.5, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.url}</span>
              <button className="t-btn" style={{ height: 28, fontSize: 12 }} onClick={() => toast.info("준비 중", "웹훅 테스트 기능은 준비 중입니다")}>테스트</button>
              <button className="t-iconbtn" style={{ width: 28, height: 28 }} onClick={() => toast.info("준비 중", "웹훅 설정 기능은 준비 중입니다")}><I.Settings size={13} /></button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "var(--text-3)" }}>이벤트:</span>
              {h.events.map((e) => <span key={e} className="t-pill info" style={{ fontSize: 10.5 }}>{e}</span>)}
              <span style={{ fontSize: 11, color: "var(--text-3)", marginLeft: "auto" }}>마지막 발송 · {h.lastFired}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── SSO sub-tab ───────────────────────────────────────────────────────────────

function SecRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "11px 18px", borderTop: "1px solid var(--border)", gap: 12, fontSize: 13 }}>
      <span style={{ width: 140, fontSize: 12.5, color: "var(--text-3)", flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>{children}</div>
    </div>
  );
}

function SsoTab() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="t-card" style={{ padding: 0 }}>
        <div style={{ padding: "16px 18px 12px", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>SAML 2.0</h3>
          <p style={{ margin: "3px 0 0", fontSize: 11.5, color: "var(--text-3)" }}>외부 ID 공급자와 SSO 연동</p>
        </div>
        <SecRow label="공급자">
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 18, height: 18, background: "#4285F4", borderRadius: 4, display: "inline-block" }} />
            Google Workspace
          </span>
        </SecRow>
        <SecRow label="Entity ID">
          <span className="mono" style={{ fontSize: 12 }}>https://timple.kr/saml/metadata</span>
        </SecRow>
        <SecRow label="ACS URL">
          <span className="mono" style={{ fontSize: 12 }}>https://timple.kr/saml/acs</span>
        </SecRow>
        <SecRow label="인증서 만료">
          <span className="mono" style={{ color: "var(--warn)" }}>2026.09.15</span>
          <span style={{ fontSize: 11, color: "var(--text-3)" }}>(114일 남음)</span>
        </SecRow>
      </div>

      <div className="t-card" style={{ padding: 0 }}>
        <div style={{ padding: "16px 18px 12px", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>SCIM 2.0</h3>
          <p style={{ margin: "3px 0 0", fontSize: 11.5, color: "var(--text-3)" }}>사용자 자동 프로비저닝</p>
        </div>
        <SecRow label="상태">
          <span className="t-pill ok">활성 · 동기화 정상</span>
          <span style={{ fontSize: 11, color: "var(--text-3)" }}>마지막 동기화 12분 전</span>
        </SecRow>
        <SecRow label="가져온 사용자">
          <span className="num" style={{ fontWeight: 600 }}>87</span>
          <span style={{ color: "var(--text-3)" }}>명</span>
        </SecRow>
      </div>
    </div>
  );
}

// ── Config (SMTP / e-tax / OpenBanking) sub-tab ──────────────────────────────

function ConfigTab() {
  const [cfg, setCfg] = useState<IntegConfig | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    api.get<IntegConfig>("/admin/integrations/config").then((r) => setCfg(r.data));
  }, []);

  const patch = <K extends keyof IntegConfig>(section: K, key: string, val: string | number | boolean) => {
    if (!cfg) return;
    setCfg({ ...cfg, [section]: { ...cfg[section], [key]: val } } as IntegConfig);
  };

  const save = async (section: "smtp" | "etax" | "openbanking") => {
    if (!cfg) return;
    setSaving(section);
    try {
      const url = section === "openbanking" ? "/admin/integrations/config/openbanking" : `/admin/integrations/config/${section}`;
      await api.patch(url, cfg[section]);
      toast.success("저장 완료");
    } catch (e: any) {
      toast.error("저장 실패", e?.response?.data?.detail ?? e?.message);
    } finally { setSaving(null); }
  };

  const testConn = async (section: "smtp" | "etax" | "openbanking") => {
    setSaving(`test_${section}`);
    try {
      const url = section === "smtp" ? "/admin/integrations/smtp/test"
        : section === "etax" ? "/admin/integrations/etax/test"
        : "/admin/integrations/banking/test";
      const r = await api.post<{ ok: boolean; message: string }>(url);
      if (r.data.ok) toast.success("연결 성공", r.data.message);
      else toast.error("연결 실패", r.data.message);
    } catch (e: any) {
      toast.error("테스트 실패", e?.response?.data?.detail ?? e?.message);
    } finally { setSaving(null); }
  };

  if (!cfg) return <div style={{ padding: 20, color: "var(--text-3)", fontSize: 13 }}>설정 로드 중…</div>;

  const SectionHeader = ({ title, sub, section }: { title: string; sub: string; section: "smtp" | "etax" | "openbanking" }) => (
    <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center" }}>
      <div>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{title}</h3>
        <p style={{ margin: "3px 0 0", fontSize: 11.5, color: "var(--text-3)" }}>{sub}</p>
      </div>
      <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
        <button
          className="t-btn t-btn-outline"
          disabled={saving === `test_${section}`}
          onClick={() => testConn(section)}
          style={{ height: 30 }}
        >
          {saving === `test_${section}` ? "테스트 중…" : "연결 테스트"}
        </button>
        <button
          className="t-btn t-btn-primary"
          disabled={saving === section}
          onClick={() => save(section)}
          style={{ height: 30 }}
        >
          {saving === section ? "저장 중…" : "저장"}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* SMTP */}
      <div className="t-card" style={{ padding: 0 }}>
        <SectionHeader title="SMTP 메일 서버" sub="외부 발신 메일 설정" section="smtp" />
        <FR label="호스트">
          <TInput value={cfg.smtp.host} onChange={(v) => patch("smtp", "host", v)} placeholder="smtp.gmail.com" mono />
        </FR>
        <FR label="포트">
          <TInput value={String(cfg.smtp.port)} onChange={(v) => patch("smtp", "port", Number(v))} placeholder="587" mono />
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, cursor: "pointer", flexShrink: 0 }}>
            <input
              type="checkbox"
              checked={cfg.smtp.tls}
              onChange={(e) => patch("smtp", "tls", e.target.checked)}
            />
            TLS
          </label>
        </FR>
        <FR label="사용자">
          <TInput value={cfg.smtp.user} onChange={(v) => patch("smtp", "user", v)} placeholder="user@example.com" />
        </FR>
        <FR label="비밀번호">
          <TInput value={cfg.smtp.password} onChange={(v) => patch("smtp", "password", v)} type="password" placeholder="변경 시에만 입력" />
        </FR>
        <FR label="발신 주소 (FROM)">
          <TInput value={cfg.smtp.from_addr} onChange={(v) => patch("smtp", "from_addr", v)} placeholder="no-reply@timple.kr" />
        </FR>
      </div>

      {/* e-tax */}
      <div className="t-card" style={{ padding: 0 }}>
        <SectionHeader title="국세청 e-tax" sub="전자세금계산서 발행 · 조회" section="etax" />
        <FR label="API URL">
          <TInput value={cfg.etax.base_url} onChange={(v) => patch("etax", "base_url", v)} placeholder="https://api.hometax.go.kr" mono />
        </FR>
        <FR label="API 키">
          <TInput value={cfg.etax.api_key} onChange={(v) => patch("etax", "api_key", v)} type="password" placeholder="변경 시에만 입력" mono />
        </FR>
        <FR label="사업자번호">
          <TInput value={cfg.etax.business_no} onChange={(v) => patch("etax", "business_no", v)} placeholder="123-45-67890" mono />
        </FR>
      </div>

      {/* OpenBanking */}
      <div className="t-card" style={{ padding: 0 }}>
        <SectionHeader title="Open Banking" sub="계좌 조회 · 이체 API" section="openbanking" />
        <FR label="Base URL">
          <TInput value={cfg.openbanking.base_url} onChange={(v) => patch("openbanking", "base_url", v)} placeholder="https://openapi.open-banking.or.kr" mono />
        </FR>
        <FR label="Client ID">
          <TInput value={cfg.openbanking.client_id} onChange={(v) => patch("openbanking", "client_id", v)} placeholder="client_id" mono />
        </FR>
        <FR label="Client Secret">
          <TInput value={cfg.openbanking.client_secret} onChange={(v) => patch("openbanking", "client_secret", v)} type="password" placeholder="변경 시에만 입력" mono />
        </FR>
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

const TABS = [
  { id: "services", l: "외부 서비스" },
  { id: "keys",     l: "API 키" },
  { id: "hooks",    l: "웹훅" },
  { id: "sso",      l: "SSO" },
  { id: "config",   l: "연동 설정" },
];

export function AdminIntegrations() {
  const [tab, setTab] = useState("services");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <TabBar tabs={TABS} active={tab} onChange={setTab} />
      {tab === "services" && <ServicesTab />}
      {tab === "keys"     && <KeysTab />}
      {tab === "hooks"    && <HooksTab />}
      {tab === "sso"      && <SsoTab />}
      {tab === "config"   && <ConfigTab />}
    </div>
  );
}
